import React, { useEffect, useState } from "react";
import { ArrowRight, Clock, MapPin, Plus, Package, Phone, Mail, Instagram, Music2, Truck } from "lucide-react";
import { api, imgUrl } from "@/lib/apiClient";
import { Header } from "@/components/Header";
import { BundleBuilder } from "@/components/BundleBuilder";
import { FlavorCard } from "@/components/FlavorCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const BUNDLE_DEFS = [
  { type: "bundle3", count: 3, tag: "Most Popular" },
  { type: "bundle6", count: 6, tag: "Great to Share" },
  { type: "bundle12", count: 12, tag: "Best Value" },
];

export default function Storefront() {
  const [flavors, setFlavors] = useState([]);
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState({ single: 5, bundle3: 13, bundle6: 26, bundle12: 54 });
  const [content, setContent] = useState({});
  const [contact, setContact] = useState({});
  const [shipping, setShipping] = useState(null);
  const [hero, setHero] = useState("");
  const [activeBundle, setActiveBundle] = useState(null);
  const { addItem } = useCart();

  const c = (key, fallback) => content[key] ?? fallback;

  useEffect(() => {
    api.get("/flavors").then((res) => {
      setFlavors(res.data.flavors);
      setProducts(res.data.products || []);
      setPrices(res.data.prices);
    }).catch(() => {});
    api.get("/settings").then((res) => {
      setContent(res.data.content || {});
      setContact(res.data.contact || {});
      setShipping(res.data.shipping || null);
      setHero(res.data.hero_image_url || "");
    }).catch(() => {});
  }, []);

  const addSingle = (flavor) => {
    addItem({ product_type: "single", label: "Single Cookie", price: Number(prices.single),
      flavors: [{ id: flavor.id, name: flavor.name }] });
    toast.success(`${flavor.name} cookie added!`);
  };

  const addProduct = (product) => {
    addItem({ product_type: "item", item_id: product.id, label: product.name, price: Number(product.price), flavors: [] });
    toast.success(`${product.name} added!`);
  };

  const openBundle = (def) => {
    setActiveBundle({ type: def.type, count: def.count, price: Number(prices[def.type]),
      label: { bundle3: "3-Pack Mix & Match", bundle6: "6-Pack Mix & Match", bundle12: "12-Pack Mix & Match" }[def.type] });
  };

  const flavorObjs = flavors.map((f) => ({ id: f.id, name: f.name, image: f.image, images: f.images }));

  const igHandle = (contact.instagram || "").replace(/^@/, "");
  const ttHandle = (contact.tiktok || "").replace(/^@/, "");

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 md:grid-cols-2 md:px-12 md:py-24">
          <div className="animate-fade-up">
            <span className="inline-block rounded-full border border-cocoa/30 bg-babybluelight px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-cocoa">
              {c("hero_badge", "Freshly Baked · Pickup Only")}
            </span>
            <h1 className="mt-6 font-heading text-5xl font-black leading-[0.95] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              {c("hero_title_1", "Warm cookies,")}
              <span className="block text-cocoa">{c("hero_title_2", "made with love.")}</span>
            </h1>
            <p className="mt-6 max-w-md text-lg text-ashydark">{c("hero_subtitle", "")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#flavors">
                <Button data-testid="hero-shop-button" className="rounded-full bg-cocoa px-8 py-6 text-base text-white transition-transform duration-300 hover:-translate-y-1 hover:bg-cocoadark">
                  {c("hero_cta_primary", "Shop Flavors")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href="#bundles">
                <Button variant="outline" data-testid="hero-bundle-button" className="rounded-full border-2 border-cocoa/40 bg-transparent px-8 py-6 text-base text-cocoa hover:bg-babybluelight">
                  {c("hero_cta_secondary", "Build a Box")}
                </Button>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -right-6 -top-6 h-40 w-40 rounded-full bg-babyblue/60 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2rem] border-4 border-white shadow-xl">
              {hero && <img src={imgUrl(hero)} alt="Cookies" className="h-[420px] w-full object-cover" data-testid="hero-image" />}
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-2xl border border-ashy/30 bg-white px-5 py-3 shadow-lg">
              <p className="font-heading text-2xl font-bold text-cocoa">${Number(prices.single).toFixed(0)}</p>
              <p className="text-xs uppercase tracking-wider text-ashydark">per cookie</p>
            </div>
          </div>
        </div>
      </section>

      {/* FLAVORS */}
      <section id="flavors" className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-24">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-cocoa">{c("flavors_eyebrow", "The Menu")}</p>
            <h2 className="mt-2 font-heading text-4xl font-black text-ink sm:text-5xl">{c("flavors_title", "Our Flavors")}</h2>
          </div>
          <p className="max-w-xs text-ashydark">{c("flavors_note", "")}</p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {flavors.map((flavor) => (
            <FlavorCard key={flavor.id} flavor={flavor} price={Number(prices.single)} onAdd={addSingle} />
          ))}
        </div>
      </section>

      {/* BUNDLES */}
      <section id="bundles" className="bg-babybluelight/60 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-cocoa">{c("bundles_eyebrow", "Save More")}</p>
            <h2 className="mt-2 font-heading text-4xl font-black text-ink sm:text-5xl">{c("bundles_title", "Build a Mix & Match Box")}</h2>
            <p className="mx-auto mt-4 max-w-xl text-ashydark">{c("bundles_subtitle", "")}</p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {BUNDLE_DEFS.map((def) => {
              const price = Number(prices[def.type]);
              return (
                <div key={def.type} data-testid={`bundle-card-${def.type}`} className="relative flex flex-col rounded-3xl border-2 border-ashy/30 bg-white p-8 transition-transform duration-300 hover:-translate-y-1">
                  <span className="absolute -top-3 left-8 rounded-full bg-cocoa px-3 py-1 text-xs font-semibold text-white">{def.tag}</span>
                  <Package className="h-10 w-10 text-cocoa" />
                  <h3 className="mt-4 font-heading text-3xl font-black text-ink">{def.count}-Pack Mix & Match</h3>
                  <p className="mt-1 text-ashydark">{def.count} cookies, any flavors</p>
                  <div className="mt-6 flex items-end gap-2">
                    <span className="font-heading text-4xl font-black text-cocoa">${price.toFixed(0)}</span>
                    <span className="mb-1 text-sm text-ashydark">(${(price / def.count).toFixed(2)}/cookie)</span>
                  </div>
                  <Button data-testid={`build-bundle-${def.type}`} onClick={() => openBundle(def)} className="mt-6 w-full rounded-full bg-cocoa py-6 text-white transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cocoadark">
                    Build this box
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTS (standalone) */}
      {products.length > 0 && (
        <section id="products" className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-24">
          <div className="mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-cocoa">{c("products_eyebrow", "More Treats")}</p>
            <h2 className="mt-2 font-heading text-4xl font-black text-ink sm:text-5xl">{c("products_title", "Also Available")}</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <div key={p.id} data-testid={`product-card-${p.id}`} className="group overflow-hidden rounded-2xl border border-ashy/30 bg-white transition-transform duration-300 hover:-translate-y-1">
                <div className="relative h-56 overflow-hidden">
                  <img src={imgUrl(p.image)} alt={p.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl font-bold text-ink">{p.name}</h3>
                  <p className="mt-2 min-h-[40px] text-sm text-ashydark">{p.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-heading text-xl font-bold text-cocoa">${Number(p.price).toFixed(2)}</span>
                    <Button data-testid={`add-product-${p.id}`} onClick={() => addProduct(p)} className="rounded-full bg-babyblue px-5 text-cocoadark transition-transform duration-300 hover:-translate-y-0.5 hover:bg-babyblue/80">
                      <Plus className="mr-1 h-4 w-4" /> Add
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PICKUP */}
      <section id="pickup" className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-6 rounded-3xl border border-ashy/30 bg-white p-8 md:grid-cols-2 md:p-12">
          <div>
            <h2 className="font-heading text-4xl font-black text-ink">{c("pickup_title", "Pickup Only")}</h2>
            <p className="mt-4 text-ashydark">{c("pickup_text", "")}</p>
          </div>
          <div className="grid gap-4">
            <div className="flex items-start gap-4 rounded-2xl bg-babybluelight/60 p-5">
              <MapPin className="mt-1 h-6 w-6 shrink-0 text-cocoa" />
              <div>
                <p className="font-semibold text-ink">{c("pickup_card1_title", "Local Pickup")}</p>
                <p className="text-sm text-ashydark">{c("pickup_card1_text", "Free — choose your pickup date & time at checkout.")}</p>
              </div>
            </div>
            {shipping && shipping.enabled ? (
              <div className="flex items-start gap-4 rounded-2xl bg-babybluelight/60 p-5">
                <Truck className="mt-1 h-6 w-6 shrink-0 text-cocoa" />
                <div>
                  <p className="font-semibold text-ink">{shipping.label || c("pickup_card2_title", "Shipping")}</p>
                  <p className="text-sm text-ashydark">{c("pickup_card2_text", "We'll ship your treats — just enter your address at checkout.")}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4 rounded-2xl bg-babybluelight/60 p-5">
                <Clock className="mt-1 h-6 w-6 shrink-0 text-cocoa" />
                <div>
                  <p className="font-semibold text-ink">{c("pickup_card2_title", "Choose Your Time")}</p>
                  <p className="text-sm text-ashydark">{c("pickup_card2_text", "Pick a date & time slot that works for you at checkout.")}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-babybluelight/60 py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-6 text-center md:px-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-cocoa">{c("contact_eyebrow", "Say Hello")}</p>
          <h2 className="mt-2 font-heading text-4xl font-black text-ink sm:text-5xl">{c("contact_title", "Get in Touch")}</h2>
          <p className="mx-auto mt-4 max-w-xl text-ashydark">{c("contact_subtitle", "")}</p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {contact.phone && (
              <a href={`tel:${contact.phone.replace(/[^0-9+]/g, "")}`} data-testid="contact-phone" className="flex flex-col items-center gap-2 rounded-2xl border border-ashy/30 bg-white p-6 transition-transform duration-300 hover:-translate-y-1">
                <Phone className="h-7 w-7 text-cocoa" />
                <span className="text-xs uppercase tracking-wider text-ashydark">Call / Text</span>
                <span className="font-semibold text-ink">{contact.phone}</span>
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} data-testid="contact-email" className="flex flex-col items-center gap-2 rounded-2xl border border-ashy/30 bg-white p-6 transition-transform duration-300 hover:-translate-y-1">
                <Mail className="h-7 w-7 text-cocoa" />
                <span className="text-xs uppercase tracking-wider text-ashydark">Email</span>
                <span className="break-all font-semibold text-ink">{contact.email}</span>
              </a>
            )}
            {ttHandle && (
              <a href={`https://www.tiktok.com/@${ttHandle}`} target="_blank" rel="noreferrer" data-testid="contact-tiktok" className="flex flex-col items-center gap-2 rounded-2xl border border-ashy/30 bg-white p-6 transition-transform duration-300 hover:-translate-y-1">
                <Music2 className="h-7 w-7 text-cocoa" />
                <span className="text-xs uppercase tracking-wider text-ashydark">TikTok</span>
                <span className="font-semibold text-ink">@{ttHandle}</span>
              </a>
            )}
            {igHandle && (
              <a href={`https://www.instagram.com/${igHandle}`} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex flex-col items-center gap-2 rounded-2xl border border-ashy/30 bg-white p-6 transition-transform duration-300 hover:-translate-y-1">
                <Instagram className="h-7 w-7 text-cocoa" />
                <span className="text-xs uppercase tracking-wider text-ashydark">Instagram</span>
                <span className="font-semibold text-ink">@{igHandle}</span>
              </a>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-ashy/30 bg-cream py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 md:flex-row md:px-12">
          <div className="text-center md:text-left">
            <p className="font-heading text-xl font-black text-cocoa">{c("brand_name", "Alexis's Sweet Treats")}</p>
            <p className="text-sm text-ashydark">{c("footer_tagline", "")}</p>
          </div>
          <a href="/admin" data-testid="footer-admin-link" className="text-xs text-ashydark transition-colors hover:text-cocoa">Owner Login</a>
        </div>
      </footer>

      <BundleBuilder product={activeBundle} flavors={flavorObjs} open={!!activeBundle} onOpenChange={(v) => !v && setActiveBundle(null)} />
    </div>
  );
}
